import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, LOCALE_ID, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { AuthService } from '@klickdata/core/auth';
import { CustomTab, CustomTabData, CustomTabPrivacy } from '@klickdata/core/custom-tab';
import { Customer } from '@klickdata/core/customer';
import { SideNaveActionsTypes } from '@klickdata/core/mobile';
import { AppScope } from '@klickdata/core/resource';
import { Filter, SelectFilterOption } from '@klickdata/core/table';
import { Utils } from '@klickdata/core/util';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
import { Observable } from 'rxjs';

@Component({
    selector: 'app-custom-tabs-editor-bottom-sheet',
    templateUrl: './custom-tabs-editor-bottom-sheet.component.html',
    styleUrls: ['./custom-tabs-editor-bottom-sheet.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomTabsEditorBottomSheetComponent extends OnDestroyHandler implements OnInit {
    SideNaveActionsTypes = SideNaveActionsTypes;
    Utils: Utils;
    public form: FormGroup;
    public customer$: Observable<Customer>;
    public appliedFilters: Filter<string | number>[] = [];
    CustomTabPrivacy = CustomTabPrivacy;
    public contextOptions: SelectFilterOption[] = [
        { title: 'My resources only', value: 'author' },
        { title: 'Academy resources only', value: 'customer' },
        { title: 'My academy and public resources', value: 'availableForCustomer' },
    ];
    constructor(
        protected fb: FormBuilder,
        protected auth: AuthService,
        protected cdr: ChangeDetectorRef,
        @Inject(LOCALE_ID) protected localeId: string,
        public bottomSheetRef: MatBottomSheetRef<CustomTabsEditorBottomSheetComponent>,
        @Inject(MAT_BOTTOM_SHEET_DATA)
        public data: { isAdmin: boolean; customTab: CustomTab; isUserPlatform: boolean; disableActiveSwitch: boolean }
    ) {
        super();
        this.form = this.fb.group({
            name: [null, Validators.required],
            order: [1],
            enabled: [true],
            required: [true],
            context: ['customer'],
        });
        this.customer$ = this.auth.getCustomer();
        this.localeId = this.localeId === 'en' ? 'en-US' : localeId;
    }

    ngOnInit(): void {
        if (this.data.customTab) {
            this.form.patchValue({
                name: this.data.customTab.metadata?.name,
                order: this.data.customTab.metadata?.order,
                enabled: !this.data.customTab.metadata?.disabled,
                required: this.data.customTab.privacy === CustomTabPrivacy.ACADEMY_REQUIRED,
            });
            this.form.get('required').setValue(this.data.customTab.privacy === CustomTabPrivacy.ACADEMY_REQUIRED);
            this.patchContext(this.data.customTab.metadata?.filters || []);
            this.data.disableActiveSwitch ? this.form.get('enabled').disable() : this.form.get('enabled').enable();
        }
    }
    public patchContext(filters: Filter<string | number>[]) {
        if (!filters?.length) return 'customer';
        const contextFilter = filters.find(
            (filter) =>
                filter.property === 'author' ||
                filter.property === 'customer' ||
                filter.property === 'availableForCustomer'
        );
        this.form.get('context').setValue(contextFilter?.property ?? 'customer');
    }
    public get isAdmin(): boolean {
        return this.data.isAdmin;
    }
    public getAppliedUXFilters(filters: Filter<string | number>[]): Filter<string | number>[] {
        if (!filters?.length) return [];
        return filters.filter(
            (filter) =>
                filter.property !== 'author' &&
                filter.property !== 'customer' &&
                filter.property !== 'availableForCustomer'
        );
    }
    public get hideFilters(): boolean {
        /**
         * if adminPlatform
         *
         * customTab
         * customTab.privacy === PUBLIC
         * -------------------------------------------
         * if userPlatform
         *
         * customTab
         * customTab.privacy !== private
         *
         */
        if (!this.data.customTab) {
            return false;
        }
        return this.data.isUserPlatform
            ? this.data.customTab.privacy !== CustomTabPrivacy.PRIVATE
            : this.data.customTab.privacy === CustomTabPrivacy.PUBLIC;
    }

    public gettranslateValue(value: any) {
        return Utils.getValueFromTranslation(value, this.localeId);
    }
    public getTabTitle() {
        return this.data.customTab
            ? this.gettranslateValue(this.data.customTab.name)
            : $localize`Create a new dashboard tab`;
    }
    onSubmit() {
        let customTab = new CustomTab();
        customTab.metadata = {
            order: this.form.value.order,
            disabled: !this.form.value.enabled,
            name: this.form.value.name,
        };
        // send filters to the backend
        if (!this.hideFilters) {
            customTab.metadata.filters = this.appliedFilters.map((filter) => ({
                property: filter.property,
                items: filter.items.join(','),
            }));
        }
        const shouldUpdatePrivacy =
            !this.data.customTab?.id || this.data.customTab?.privacy !== CustomTabPrivacy.PUBLIC;

        if (shouldUpdatePrivacy) {
            customTab.privacy = this.data.isUserPlatform
                ? CustomTabPrivacy.PRIVATE
                : this.form.value.required
                ? CustomTabPrivacy.ACADEMY_REQUIRED
                : CustomTabPrivacy.ACADEMY;
        }
        if (!this.data.customTab?.id) {
            customTab.scope_id = AppScope.RESOURCES;
        }
        this.handleContext(customTab);
        this.bottomSheetRef.dismiss(customTab);
        this.form.reset();
    }
    private handleContext(customTab: CustomTab) {
        if (this.hideFilters) return;
        const contextFilter =
            this.form.value.context === 'author'
                ? { property: 'author', items: ['{{author_id}}'] }
                : this.form.value.context === 'customer'
                ? { property: 'customer', items: ['{{customer_id}}'] }
                : { property: 'availableForCustomer', items: ['{{customer_id}}'] };

        if (!!customTab.metadata?.filters?.length) {
            customTab.metadata.filters.push(contextFilter);
        } else {
            customTab.metadata.filters = [contextFilter];
        }
    }

    onCancel() {
        this.bottomSheetRef.dismiss(false);
    }
}
